import { useMemo, useState } from 'react';
import {
  ModalLayout,
  SegmentedControl,
  TextArea,
  Checkbox,
  Button,
  useToast,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { MOCK_PEOPLE } from '../../constants/people.js';
import AttestationRecipientPicker from './AttestationRecipientPicker.jsx';
import { ShieldCheck } from 'lucide-react';

const MODE = { FORMAL: 'formal', INFORMAL: 'informal' };

const FIELD_LABEL = 'mb-1.5 block text-[11.5px] font-semibold uppercase tracking-wide text-content-faint';

function defaultStatement(event, t) {
  if (!event) return '';
  return t('attestation.modal.statementDefault', {
    id: event.id,
    title: event.title,
  });
}

/**
 * aims-htl “Request Formal Verification” / “Ask a Teammate” modal (atm-*).
 * Opened from Attestation → Request verification. Mock submit for V1.
 * Mount only while open (parent) so form state resets without an effect.
 */
function RequestVerificationModal({ isOpen, event, onClose }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const [mode, setMode] = useState(MODE.FORMAL);
  const [recipientId, setRecipientId] = useState(null);
  const [statement, setStatement] = useState(() => defaultStatement(event, t));
  const [question, setQuestion] = useState('');
  const [attachContext, setAttachContext] = useState(true);
  const [requestByEod, setRequestByEod] = useState(false);
  const [notifyCompliance, setNotifyCompliance] = useState(false);

  const isFormal = mode === MODE.FORMAL;

  const modeOptions = useMemo(
    () => [
      { value: MODE.FORMAL, label: t('attestation.modal.modeFormal') },
      { value: MODE.INFORMAL, label: t('attestation.modal.modeInformal') },
    ],
    [t],
  );

  const canSubmit = Boolean(recipientId) && (isFormal ? statement.trim() : question.trim());

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const recipient = MOCK_PEOPLE.find((person) => person.id === recipientId);
    toast.success({
      title: isFormal ? t('attestation.modal.createdTitle') : t('attestation.modal.sentTitle'),
      message: isFormal
        ? t('attestation.modal.createdMessage', { recipient: recipient?.name ?? '' })
        : t('attestation.modal.sentMessage', { recipient: recipient?.name ?? '' }),
    });
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      useBodyPortal
      titlePrefix={<ShieldCheck size={20} className="text-sky-500" />}
      title={isFormal ? t('attestation.modal.formalTitle') : t('attestation.modal.informalTitle')}
      subtitle={event ? `${event.id} · ${event.title}` : undefined}
      maxWidthClassName="max-w-[560px]"
      heightClassName="max-h-[min(90vh,800px)]"
      fillHeight
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-aimsos-auto-sm">
          <div className="flex flex-col gap-4">
            <SegmentedControl
              value={mode}
              onChange={setMode}
              options={modeOptions}
              ariaLabel={t('attestation.modal.modeAria')}
              className="!w-full"
              itemClassName="!px-3 !py-1.5 text-[12.5px]"
            />

            {isFormal ? (
              <div className="rounded-lg border border-brand-500/25 bg-brand-500/5 px-3 py-3">
                <p className="text-[11.5px] font-semibold text-brand-500">
                  {t('attestation.modal.recordedTitle')}
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-content-muted">
                  <li>{t('attestation.modal.recordedHash')}</li>
                  <li>{t('attestation.modal.recordedLinked')}</li>
                  <li>{t('attestation.modal.recordedSealed')}</li>
                  <li>{t('attestation.modal.recordedVisible')}</li>
                </ul>
              </div>
            ) : null}

            <AttestationRecipientPicker value={recipientId} onChange={setRecipientId} />

            {isFormal ? (
              <>
                <div>
                  <label className={FIELD_LABEL} htmlFor="attestation-statement">
                    {t('attestation.modal.statementLabel')}
                  </label>
                  <TextArea
                    id="attestation-statement"
                    rows={4}
                    value={statement}
                    onChange={(changeEvent) => setStatement(changeEvent.target.value)}
                    placeholder={t('attestation.modal.statementPlaceholder')}
                  />
                </div>

                <div className="flex flex-col gap-2.5">
                  <label className="flex items-center gap-2 text-xs text-content">
                    <Checkbox
                      size="small"
                      checked={attachContext}
                      onChange={() => setAttachContext((value) => !value)}
                      aria-label={t('attestation.modal.optAttach')}
                    />
                    {t('attestation.modal.optAttach')}
                  </label>
                  <label className="flex items-center gap-2 text-xs text-content">
                    <Checkbox
                      size="small"
                      checked={requestByEod}
                      onChange={() => setRequestByEod((value) => !value)}
                      aria-label={t('attestation.modal.optEod')}
                    />
                    {t('attestation.modal.optEod')}
                  </label>
                  <label className="flex items-center gap-2 text-xs text-content">
                    <Checkbox
                      size="small"
                      checked={notifyCompliance}
                      onChange={() => setNotifyCompliance((value) => !value)}
                      aria-label={t('attestation.modal.optNotify')}
                    />
                    {t('attestation.modal.optNotify')}
                  </label>
                </div>
              </>
            ) : (
              <div>
                <label className={FIELD_LABEL} htmlFor="attestation-question">
                  {t('attestation.modal.questionLabel')}
                </label>
                <TextArea
                  id="attestation-question"
                  rows={4}
                  value={question}
                  onChange={(changeEvent) => setQuestion(changeEvent.target.value)}
                  placeholder={t('attestation.modal.questionPlaceholder')}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-edge px-5 py-3.5">
          <Button variant="secondary" size="sm" onClick={handleClose}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary" size="sm" disabled={!canSubmit} onClick={handleSubmit}>
            {isFormal ? t('attestation.modal.create') : t('attestation.modal.send')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}

export default RequestVerificationModal;
